/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, March 30th 2022, 9:43:40 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { TaskTemplate } from "lib/schemas";
import { TextFormInput, SwitchFormInput, FormOptionsSeparator } from "ui-widgets/forms";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { InputHelpDialogWrapper, EmphasisText } from "ui-widgets";
import { TaskTemplatesTransferListInput } from "./TaskTemplateInput";

export const UserTaskCommonSettings = () => {
  const { watch } = useOSFormContext<TaskTemplate>();
  const [taskTemplateId, processDefinitionId, cancel_active] = watch<(keyof TaskTemplate)[]>([
    "id",
    "process",
    "cancel_active",
  ]) as [TaskTemplate["id"], TaskTemplate["process"], TaskTemplate["cancel_active"]];

  return (
    <>
      <h3>
        <EmphasisText>User Task Constraints</EmphasisText>
      </h3>

      <InputHelpDialogWrapper
        helpTitle="Maximum Duration"
        helpContent="Set a maximum duration in days that a user will have to complete this task. This setting helps track delays in tasks."
      >
        <TextFormInput<TaskTemplate> schema_field_key="maximum_duration" label="Maximum duration" type="number" />
      </InputHelpDialogWrapper>

      <InputHelpDialogWrapper
        helpTitle="Client Task"
        helpContent="If set to true, this task will only be carried out by the user who made the initial submission for the process to start or anyone they choose to assign it to."
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="client_task"
          label="Task should be carried out by the submission owner"
        />
      </InputHelpDialogWrapper>

      <InputHelpDialogWrapper
        helpTitle="Redirect on Start"
        helpContent="If set to true, the user who initiated the process will be redirected to this task only if it is active."
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="auto_redirect_on_start"
          label="Automatically redirect the submitter to this task if it is active and it proceeds the process start"
        />
      </InputHelpDialogWrapper>

      <h3>
        <EmphasisText>User Task Side Effects</EmphasisText>
      </h3>

      <InputHelpDialogWrapper
        helpTitle="Cancel Active"
        helpContent="If set to true, any other user task that is active will be cancelled. Use this setting if you want a user to choose between to actions a the same stage of your workflow."
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="cancel_active"
          label="Cancel all other active user tasks when this one is executed"
        />
      </InputHelpDialogWrapper>
      {!cancel_active && taskTemplateId && (
        <>
          <FormOptionsSeparator seperatorText="OR" />
          <TaskTemplatesTransferListInput<TaskTemplate>
            schema_field_key="active_tasks_to_cancel"
            apiQueryParams={{
              process_definition: processDefinitionId,
              type: "View",
              exclude: [taskTemplateId],
            }}
            label="Choose specific active tasks to cancel when this task is executed"
            helperText="The specific active user task thats you choose here will be cancelled. Use this setting if you want a user to choose between to actions a the same stage of your workflow"
          />
        </>
      )}
    </>
  );
};
